import { FileCheck, MailCheck, type LucideIcon } from 'lucide-react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { InsertionOrder } from 'src/redux/billing/billing.types';
import {
  formatInsertionOrderStatusLabel,
  getInsertionOrderDisplayStatus,
  getInsertionOrderStatusBadgeVariant,
} from '../../utils/insertion-order-status';

interface StatusTooltipIconProps {
  icon: LucideIcon;
  isActive: boolean;
  tooltipMessage: string;
}

const StatusTooltipIcon = ({ icon: Icon, isActive, tooltipMessage }: StatusTooltipIconProps) => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger>
        <Icon
          className={`h-4 w-4 ${isActive ? 'text-[#2563EB]' : 'text-[#9CA3AF]'}`}
          strokeWidth={2.5}
        />
      </TooltipTrigger>
      <TooltipContent className="px-3 py-1">
        <p className="text-xs">
          {tooltipMessage}
        </p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface IoBoldsignStatusCellProps {
  order: InsertionOrder;
}

export function IoBoldsignStatusCell({ order }: IoBoldsignStatusCellProps) {
  const status = getInsertionOrderDisplayStatus(order);

  if (status !== 'sent') {
    const statusMessage = order.boldsign_status_message;
    const statusBadge = (
      <PortalDsBadge
        pill
        variant={getInsertionOrderStatusBadgeVariant(status)}
      >
        {formatInsertionOrderStatusLabel(status)}
      </PortalDsBadge>
    );

    if (statusMessage) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              {statusBadge}
            </TooltipTrigger>
            <TooltipContent className="max-w-xs px-3 py-1">
              <p className="text-xs">
                {statusMessage}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return statusBadge;
  }

  // Design-system gap: dual signer delivery/signed icons are not specified
  // in design-system.html; restyled with DS colours only.
  return (
    <span className="inline-flex items-center rounded-full border-[1.5px] border-[#EDEEF2] bg-white px-2 py-1">
      <div className="flex items-center gap-1">
        <div className="mr-1 flex flex-col items-center gap-0.5">
          <div className="flex items-center gap-1">
            <StatusTooltipIcon
              icon={MailCheck}
              isActive={order.client_delivered || order.client_signed}
              tooltipMessage={order.client_delivered ? `Delivered to ${order.client_email}` : `Awaiting delivery to ${order.client_email}`}
            />
            <StatusTooltipIcon
              icon={FileCheck}
              isActive={order.client_signed}
              tooltipMessage={order.client_signed ? `Signed by ${order.client_email}` : `Awaiting signature from ${order.client_email}`}
            />
          </div>
          <span className="text-[8px] font-medium leading-none text-[#9CA3AF]">
            Client
          </span>
        </div>

        <div className="mr-1 flex flex-col items-center gap-0.5">
          <div className="flex items-center gap-1">
            <StatusTooltipIcon
              icon={MailCheck}
              isActive={order.agency_delivered || order.agency_signed}
              tooltipMessage={order.agency_delivered ? `Delivered to ${order.agent_email}` : `Awaiting delivery to ${order.agent_email}`}
            />
            <StatusTooltipIcon
              icon={FileCheck}
              isActive={order.agency_signed}
              tooltipMessage={order.agency_signed ? `Signed by ${order.agent_email}` : `Awaiting signature from ${order.agent_email}`}
            />
          </div>
          <span className="text-[8px] font-medium leading-none text-[#9CA3AF]">
            Internal
          </span>
        </div>
      </div>
    </span>
  );
}
